import type { Product } from '@/arcade'

export const IAP_CATALOG: Product[] = [
  { id: 'gems_mini', title: 'Gem Pebble', description: '20 gems to get you started.', priceLabel: '$0.99', grant: { gems: 20 } },
  { id: 'gems_small', title: 'Handful of Gems', description: '50 gems for continues and rare skins.', priceLabel: '$1.99', grant: { gems: 50 } },
  { id: 'gems_large', title: 'Vault of Gems', description: '350 gems with a healthy bonus.', priceLabel: '$9.99', grant: { gems: 350 } },
  { id: 'gems_grand', title: 'Grand Vault', description: '1,000 gems for serious collectors.', priceLabel: '$24.99', grant: { gems: 1000 } },
  { id: 'gems_royal', title: 'Royal Vault', description: '2,400 gems, the deepest vault.', priceLabel: '$49.99', grant: { gems: 2400 } },
  { id: 'coins_small', title: 'Pouch of Coins', description: '500 coins to spend on cosmetics and powerups.', priceLabel: '$0.99', grant: { coins: 500 } },
  { id: 'coins_large', title: 'Chest of Coins', description: '3,000 coins — the cozy grinder pack.', priceLabel: '$4.99', grant: { coins: 3000 } },
  { id: 'remove_ads', title: 'Cozy Pass (Remove Ads)', description: 'Removes every ad surface, forever.', priceLabel: '$3.99', grant: { removeAds: 1 } },
  { id: 'starter_bundle', title: "Wanderer's Bundle", description: '1,000 coins, 30 gems, and Remove Ads.', priceLabel: '$4.99', grant: { coins: 1000, gems: 30, removeAds: 1 } },
  { id: 'skin_pack_aurora', title: 'Aurora Skin Pack', description: 'Unlock the shimmering Aurora Ember set.', priceLabel: '$2.99', grant: { skin_aurora: 1 } },
  { id: 'perm_coin_doubler', title: 'Eternal Coin Doubler', description: 'Permanently doubles every coin you bank. Forever.', priceLabel: '$9.99', grant: { perm_double_coins: 1 } },
  { id: 'perm_magnet', title: 'Eternal Magnet', description: 'Permanent coin magnet aura on every stop. Forever.', priceLabel: '$7.99', grant: { perm_magnet: 1 } },
  { id: 'prestige_aurora', title: 'Aurora Luminary', description: 'Exclusive legendary character with aurora shader, plus 100 gems.', priceLabel: '$19.99', grant: { character_aurora_luminary: 1, gems: 100 } },
  { id: 'mega_bundle', title: 'Homecoming Mega Bundle', description: '20,000 coins, 2,500 gems, Remove Ads, the Aurora Luminary, and the full legendary prop set.', priceLabel: '$99.99', grant: { coins: 20000, gems: 2500, removeAds: 1, character_aurora_luminary: 1, prop_jetpack: 1, prop_dragon_wings: 1, prop_ghost_pendant: 1 } },
]

export type PowerupTier = 'basic' | 'premium' | 'legendary'
export type PowerupCategory = 'consumable' | 'permanent' | 'lives'

export interface PowerupDef {
  id: string
  name: string
  description: string
  icon: string
  cost: number
  currency: 'coins' | 'gems'
  tier: PowerupTier
  category: PowerupCategory
  maxQuantity: number
  unlockLevel: number
}

export const POWERUPS: PowerupDef[] = [
  { id: 'shield', name: 'Shield', description: 'Survive one hazard hit.', icon: 'shield', cost: 120, currency: 'coins', tier: 'basic', category: 'consumable', maxQuantity: 5, unlockLevel: 0 },
  { id: 'rewind', name: 'Undo', description: 'Undo your last slide.', icon: 'restart', cost: 90, currency: 'coins', tier: 'basic', category: 'consumable', maxQuantity: 10, unlockLevel: 0 },
  { id: 'hint', name: 'Path Hint', description: 'Reveal one optimal slide.', icon: 'hint', cost: 150, currency: 'coins', tier: 'basic', category: 'consumable', maxQuantity: 5, unlockLevel: 0 },
  { id: 'magnet', name: 'Coin Magnet', description: 'Vacuum every coin on the board.', icon: 'magnet', cost: 200, currency: 'coins', tier: 'premium', category: 'consumable', maxQuantity: 5, unlockLevel: 3 },
  { id: 'slowmo', name: 'Slow-Mo', description: 'Slow the slide to read the board.', icon: 'clock', cost: 80, currency: 'coins', tier: 'basic', category: 'consumable', maxQuantity: 5, unlockLevel: 0 },
  { id: 'extra_lives', name: 'Extra Lives', description: 'Refill your lives to full.', icon: 'heart', cost: 20, currency: 'gems', tier: 'premium', category: 'lives', maxQuantity: 3, unlockLevel: 0 },
  { id: 'double_coins', name: 'Double Coins', description: 'Bank 2x coins from this run.', icon: 'doubleCoin', cost: 25, currency: 'gems', tier: 'premium', category: 'consumable', maxQuantity: 5, unlockLevel: 5 },
  { id: 'invincibility', name: 'Invincibility', description: 'Hazards cannot kill you for 3 slides.', icon: 'invincibility', cost: 35, currency: 'gems', tier: 'legendary', category: 'consumable', maxQuantity: 3, unlockLevel: 8 },
  { id: 'score_multiplier', name: 'Score Surge', description: 'Double the score of this run.', icon: 'score', cost: 30, currency: 'gems', tier: 'premium', category: 'consumable', maxQuantity: 5, unlockLevel: 10 },
  { id: 'skip_level', name: 'Skip Level', description: 'Mark this level complete at 0 stars and move on.', icon: 'skip', cost: 50, currency: 'gems', tier: 'legendary', category: 'consumable', maxQuantity: 2, unlockLevel: 12 },
]

export interface CosmeticDef {
  id: string
  name: string
  kind: 'skin' | 'trail' | 'prop'
  description: string
  cost: number
  currency: 'coins' | 'gems'
  color: string
}

export const COSMETICS: CosmeticDef[] = [
  { id: 'ember', name: 'Ember', kind: 'skin', description: 'The warm original glow.', cost: 0, currency: 'coins', color: '#ffb454' },
  { id: 'moss', name: 'Moss', kind: 'skin', description: 'A forest-soft green blob.', cost: 400, currency: 'coins', color: '#5fd38b' },
  { id: 'frost', name: 'Frost', kind: 'skin', description: 'Cool cyan crystalline sheen.', cost: 600, currency: 'coins', color: '#5fd6ff' },
  { id: 'ember_aurora', name: 'Aurora', kind: 'skin', description: 'Shifting aurora gradient.', cost: 40, currency: 'gems', color: '#e56aff' },
  { id: 'none', name: 'No Trail', kind: 'trail', description: 'Clean and simple.', cost: 0, currency: 'coins', color: '#7a7599' },
  { id: 'spark', name: 'Spark Trail', kind: 'trail', description: 'Glowing spark ribbon.', cost: 300, currency: 'coins', color: '#ffcf5c' },
  { id: 'bubble', name: 'Bubble Trail', kind: 'trail', description: 'Soft rising bubbles.', cost: 350, currency: 'coins', color: '#7de3ff' },
]

export const PRODUCT_ICONS: Record<string, string> = {
  gems_mini: 'gem',
  gems_small: 'gems',
  gems_large: 'gems',
  gems_grand: 'gems',
  gems_royal: 'gems',
  coins_small: 'coin',
  coins_large: 'coins',
  remove_ads: 'success',
  starter_bundle: 'gift',
  skin_pack_aurora: 'palette',
  perm_coin_doubler: 'doubleCoin',
  perm_magnet: 'magnet',
  prestige_aurora: 'vip',
  mega_bundle: 'trophy',
}

export interface VipTierDef {
  tier: number
  name: string
  minSpendPoints: number
}

export const VIP_TIERS: VipTierDef[] = [
  { tier: 0, name: 'Wanderer', minSpendPoints: 0 },
  { tier: 1, name: 'Bronze Ember', minSpendPoints: 2000 },
  { tier: 2, name: 'Silver Ember', minSpendPoints: 8000 },
  { tier: 3, name: 'Gold Ember', minSpendPoints: 25000 },
  { tier: 4, name: 'Aurora Patron', minSpendPoints: 75000 },
]

export function spendPoints(totalSpent: { coins: number; gems: number }): number {
  return totalSpent.coins + totalSpent.gems * 25
}

export function computeVipTier(totalSpent: { coins: number; gems: number }): number {
  const points = spendPoints(totalSpent)
  let tier = 0
  for (const t of VIP_TIERS) {
    if (points >= t.minSpendPoints) tier = t.tier
  }
  return tier
}

export function vipTierName(tier: number): string {
  return VIP_TIERS.find((t) => t.tier === tier)?.name ?? VIP_TIERS[0].name
}
